import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import { AppState } from 'src/reducers';
import { Campaign as ActiveCampaign } from 'src/types';

type ProductId = number;
type CampaignId = number;

type ElasticResponse = {
  hits?: {
    hits: ElasticHit[];
  };
};

/* eslint-disable camelcase */
export type ElasticHit = {
  _source: {
    product: {
      id: ProductId;
      mobile_web_uri?: string;
      price?: { count: number };
      statistics?: {
        sparklines?: {
          lowest_price?: { number_of_days: number; values?: number[] };
        };
      };
    };
    campaign: { id: CampaignId };
    price?: {
      days_ago_compare?: number;
      diff_percentage: number;
      display?: { offer: string; previousPrice: string };
      offer?: number;
      offer_incl_shipping?: number;
    };
    priceInfo?: {
      priceId?: number;
    };
    store?: { name: string; logo: { 88: string; 176: string }; id: number };
  };
};

type Campaign = {
  campaignId: number;
  diffPercentage?: number;
  priceId?: number;
};

export type ActiveCampaignByProductId = {
  [productId: number]: Campaign;
};

export const useActiveCampaignByProductId = (
  productIds: number[],
  includePrice?: boolean
) => {
  const [result, setResult] = useState<ActiveCampaignByProductId>({});
  const { market } = useContext(AppContext);

  const activeCampaign = useSelector((state: AppState) =>
    state.globals.campaigns.active.find(
      (c: ActiveCampaign) => c.type === 'OFFER'
    )
  );

  useEffect(() => {
    if (!IS_BROWSER || !productIds.length) {
      return;
    }

    const dealsEndpoint = `${market.campaignsBaseUrl}/campaign-${market.code}-*-deals/_search`;
    const source = ['product.id', 'campaign.id'];
    fetch(dealsEndpoint, {
      method: 'POST',
      body: JSON.stringify({
        _source: includePrice
          ? [...source, 'price', 'priceInfo.priceId']
          : source,
        size: 100,
        query: {
          bool: {
            filter: {
              terms: { 'product.id': productIds },
            },
          },
        },
      }),
    })
      .catch(err => {
        throw new Error(`FetchError: ${err.message} ${dealsEndpoint}`);
      })
      .then(res => res.json())
      .then((res: ElasticResponse) => {
        if (!res.hits?.hits) return;

        const hits = res.hits.hits.map(hit => ({
          productId: hit._source.product.id,
          campaignId: hit._source.campaign.id,
          diffPercentage: hit._source.price?.diff_percentage,
          priceId: hit._source.priceInfo?.priceId,
        }));

        const getActiveCampaignForProduct = (productId: ProductId) =>
          hits
            .filter(hit => hit.productId === productId)
            .map(hit => ({
              campaignId: hit.campaignId,
              diffPercentage: hit.diffPercentage,
              priceId: hit.priceId,
            }))
            .find(
              campaign => campaign.campaignId === activeCampaign.id
            ) as Campaign;

        const activeCampaignByProductId = hits.reduce(
          (acc, hit) => ({
            ...acc,
            [hit.productId]: getActiveCampaignForProduct(hit.productId),
          }),
          {} as ActiveCampaignByProductId
        );

        setResult(activeCampaignByProductId);
      });
  }, [
    activeCampaign.id,
    includePrice,
    market.campaignsBaseUrl,
    market.code,
    productIds,
  ]);

  return result;
};
